import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload,
  AlertCircle,
  X,
  CheckCircle,
  Eye,
  Trash2,
  CreditCard,
  BookOpen,
} from 'lucide-react';

// Document types
export type DocumentType =
  | 'CIN_FRONT'
  | 'CIN_BACK'
  | 'PASSPORT'
  | 'CV'
  | 'PHOTO'
  // Staff-specific documents
  | 'CONTRAT_TRAVAIL'
  | 'DIPLOME_SCAN'
  | 'ATTESTATION_RIB'
  // Professor-specific documents (E-DEP)
  | 'AUTORISATION_ENSEIGNER' // For vacataires from public institutions - renewable annually
  | 'ENGAGEMENT_PROFESSEUR'; // Signed engagement - renewable annually (for all professors)

// ID Document type selection
export type IdDocumentChoice = 'CIN' | 'PASSPORT';

export interface UploadedDocument {
  type: DocumentType;
  file: File;
  previewUrl: string;
}

interface DocumentUploadProps {
  onDocumentsChange: (documents: UploadedDocument[]) => void;
  onIdDocumentTypeChange?: (type: IdDocumentChoice) => void;
  initialIdDocumentType?: IdDocumentChoice;
  isStudent?: boolean;
  isStaff?: boolean;
  isProfesseur?: boolean; // Professor role
  isVacataire?: boolean; // Vacataire status
  isFromPublicInstitution?: boolean; // From public higher education institution
  currentAcademicYear?: string; // Current academic year for renewal tracking
  engagementTemplateUrl?: string; // URL to download engagement template
  existingDocuments?: UploadedDocument[];
  className?: string;
  makeAllOptional?: boolean; // Override all required fields to be optional (for DG)
}

interface DocumentRowProps {
  type: DocumentType;
  label: string;
  required: boolean;
  document?: UploadedDocument;
  onUpload: (file: File) => void;
  onRemove: () => void;
  onPreview: () => void;
}

const DocumentRow: React.FC<DocumentRowProps> = ({
  label,
  required,
  document,
  onUpload,
  onRemove,
  onPreview,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
    // Reset input
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {document ? (
          <>
            <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
              Téléchargé
            </span>
            <button
              type="button"
              onClick={onPreview}
              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded transition-colors"
              title="Voir le document"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        ) : (
          <label className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 bg-primary-50 hover:bg-primary-100 rounded cursor-pointer transition-colors">
            <Upload className="w-4 h-4" />
            <span>Télécharger</span>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        )}
      </div>
    </div>
  );
};

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onDocumentsChange,
  onIdDocumentTypeChange,
  initialIdDocumentType,
  isStudent = false,
  isStaff = false,
  isProfesseur = false,
  isVacataire = false,
  isFromPublicInstitution = false,
  currentAcademicYear,
  engagementTemplateUrl,
  existingDocuments = [],
  className = '',
  makeAllOptional = false,
}) => {
  const [documents, setDocuments] = useState<UploadedDocument[]>(existingDocuments);
  const [previewDocument, setPreviewDocument] = useState<UploadedDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Determine initial ID document type based on existing documents or prop
  const getInitialIdDocType = (): IdDocumentChoice => {
    // First check existing documents
    const hasCIN = existingDocuments.some(d => d.type === 'CIN_FRONT' || d.type === 'CIN_BACK');
    const hasPassport = existingDocuments.some(d => d.type === 'PASSPORT');

    if (hasCIN) return 'CIN';
    if (hasPassport) return 'PASSPORT';

    // Then use prop or default to CIN
    return initialIdDocumentType || 'CIN';
  };

  const [idDocumentType, setIdDocumentType] = useState<IdDocumentChoice>(getInitialIdDocType);

  // Sync documents when existingDocuments prop changes (e.g., after loading from IndexedDB)
  useEffect(() => {
    if (existingDocuments.length > 0 && documents.length === 0) {
      setDocuments(existingDocuments);
      // Also update ID document type based on loaded documents
      const hasCIN = existingDocuments.some(d => d.type === 'CIN_FRONT' || d.type === 'CIN_BACK');
      const hasPassport = existingDocuments.some(d => d.type === 'PASSPORT');
      if (hasCIN) {
        setIdDocumentType('CIN');
      } else if (hasPassport) {
        setIdDocumentType('PASSPORT');
      }
    }
  }, [existingDocuments, documents.length]);

  // Update parent when ID document type changes
  useEffect(() => {
    if (onIdDocumentTypeChange) {
      onIdDocumentTypeChange(idDocumentType);
    }
  }, [idDocumentType, onIdDocumentTypeChange]);

  const handleIdDocumentTypeChange = (newType: IdDocumentChoice) => {
    // Just change the view, don't delete the documents
    // Documents are preserved so user can switch back and see them
    setIdDocumentType(newType);
  };

  const handleUpload = useCallback((type: DocumentType, file: File) => {
    setError(null);

    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      setError('Le fichier est trop grand (max 5MB)');
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    const newDocument: UploadedDocument = {
      type,
      file,
      previewUrl,
    };

    setDocuments(prev => {
      const filtered = prev.filter(d => d.type !== type);
      const updated = [...filtered, newDocument];
      onDocumentsChange(updated);
      return updated;
    });
  }, [onDocumentsChange]);

  const handleRemove = useCallback((type: DocumentType) => {
    setDocuments(prev => {
      const doc = prev.find(d => d.type === type);
      if (doc) {
        URL.revokeObjectURL(doc.previewUrl);
      }
      const updated = prev.filter(d => d.type !== type);
      onDocumentsChange(updated);
      return updated;
    });
  }, [onDocumentsChange]);

  const getDocument = (type: DocumentType) => documents.find(d => d.type === type);

  // Build document list based on user's choice (not country of origin)
  // User chooses: CIN (front + back) OR Passport
  // These are mutually exclusive by user choice
  const idDocs: { type: DocumentType; label: string; required: boolean }[] = [];

  if (idDocumentType === 'CIN') {
    idDocs.push(
      { type: 'CIN_FRONT', label: 'CIN - Recto (face avant)', required: !makeAllOptional },
      { type: 'CIN_BACK', label: 'CIN - Verso (face arrière)', required: !makeAllOptional }
    );
  } else {
    idDocs.push(
      { type: 'PASSPORT', label: 'Passeport - Première page (page d\'identité)', required: !makeAllOptional }
    );
  }

  // Other documents (only for non-staff users)
  const otherDocs: { type: DocumentType; label: string; required: boolean }[] = [];

  // For non-staff users, show CV and Photo in "Autres documents"
  if (!isStaff) {
    // CV is required for non-students (optional if makeAllOptional)
    if (!isStudent) {
      otherDocs.push({ type: 'CV', label: 'CV (Curriculum Vitae)', required: !makeAllOptional });
    }
    // Photo is optional
    otherDocs.push({ type: 'PHOTO', label: 'Photo d\'identité', required: false });
  }

  // Staff-specific professional documents (required for staff, optional if makeAllOptional)
  const staffDocs: { type: DocumentType; label: string; required: boolean }[] = [];
  if (isStaff) {
    staffDocs.push(
      { type: 'CV', label: 'CV (Curriculum Vitae)', required: !makeAllOptional },
      { type: 'PHOTO', label: 'Photo d\'identité', required: false },
      { type: 'CONTRAT_TRAVAIL', label: 'Contrat de travail (version scannée)', required: !makeAllOptional },
      { type: 'DIPLOME_SCAN', label: 'Dernier diplôme (version scannée)', required: !makeAllOptional },
      { type: 'ATTESTATION_RIB', label: 'Attestation de RIB', required: !makeAllOptional }
    );
  }

  // Professor-specific documents (E-DEP requirements, optional if makeAllOptional)
  const professorDocs: { type: DocumentType; label: string; required: boolean; description?: string }[] = [];
  if (isProfesseur) {
    // Engagement is required for ALL professors (permanent and vacataire)
    professorDocs.push({
      type: 'ENGAGEMENT_PROFESSEUR',
      label: `Engagement signé${currentAcademicYear ? ` (${currentAcademicYear})` : ''}`,
      required: !makeAllOptional,
      description: 'Document à renouveler chaque année académique'
    });

    // Authorization is required ONLY for vacataires from public institutions
    if (isVacataire && isFromPublicInstitution) {
      professorDocs.push({
        type: 'AUTORISATION_ENSEIGNER',
        label: `Autorisation d'enseigner${currentAcademicYear ? ` (${currentAcademicYear})` : ''}`,
        required: !makeAllOptional,
        description: 'Autorisation de votre établissement public d\'origine - à renouveler chaque année'
      });
    }
  }

  // Check completion of required ID docs
  const idDocsComplete = idDocs.filter(d => d.required).every(doc => getDocument(doc.type));
  const otherDocsComplete = otherDocs.filter(d => d.required).every(doc => getDocument(doc.type));
  const staffDocsComplete = staffDocs.filter(d => d.required).every(doc => getDocument(doc.type));
  const professorDocsComplete = professorDocs.filter(d => d.required).every(doc => getDocument(doc.type));

  return (
    <div className={`${className}`}>
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto p-1 hover:bg-red-100 rounded"
          >
            <X className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}

      {/* Info box */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Document d'identité requis:</strong> Veuillez choisir le type de document à télécharger (CIN ou Passeport).
          Vous pouvez utiliser une CIN marocaine ou un passeport selon votre situation.
        </p>
      </div>

      {/* ID Document Type Selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Type de document d'identité <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleIdDocumentTypeChange('CIN')}
            className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
              idDocumentType === 'CIN'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <CreditCard className="w-5 h-5" />
            <span className="font-medium">CIN Marocaine</span>
          </button>
          <button
            type="button"
            onClick={() => handleIdDocumentTypeChange('PASSPORT')}
            className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
              idDocumentType === 'PASSPORT'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <BookOpen className="w-5 h-5" />
            <span className="font-medium">Passeport</span>
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {idDocumentType === 'CIN'
            ? 'Téléchargez le recto (face avant) et le verso (face arrière) de votre CIN'
            : 'Téléchargez la première page de votre passeport (page d\'identité avec photo)'}
        </p>
      </div>

      {/* Status indicator */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">
          Documents d'identité ({idDocumentType === 'CIN' ? 'CIN' : 'Passeport'})
        </span>
        {idDocsComplete && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Complet
          </span>
        )}
      </div>

      {/* ID Document list */}
      <div className="bg-gray-50 rounded-lg px-4 mb-4">
        {idDocs.map(doc => (
          <DocumentRow
            key={doc.type}
            type={doc.type}
            label={doc.label}
            required={doc.required}
            document={getDocument(doc.type)}
            onUpload={(file) => handleUpload(doc.type, file)}
            onRemove={() => handleRemove(doc.type)}
            onPreview={() => setPreviewDocument(getDocument(doc.type) || null)}
          />
        ))}
      </div>

      {/* Other documents */}
      {otherDocs.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              Autres documents
            </span>
            {otherDocsComplete && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Complet
              </span>
            )}
          </div>
          <div className="bg-gray-50 rounded-lg px-4">
            {otherDocs.map(doc => (
              <DocumentRow
                key={doc.type}
                type={doc.type}
                label={doc.label}
                required={doc.required}
                document={getDocument(doc.type)}
                onUpload={(file) => handleUpload(doc.type, file)}
                onRemove={() => handleRemove(doc.type)}
                onPreview={() => setPreviewDocument(getDocument(doc.type) || null)}
              />
            ))}
          </div>
        </>
      )}

      {/* Staff Professional Documents (only for staff users) */}
      {staffDocs.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3 mt-4">
            <span className="text-sm font-medium text-gray-700">
              Documents professionnels
            </span>
            {staffDocsComplete && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Complet
              </span>
            )}
          </div>
          <div className="bg-gray-50 rounded-lg px-4">
            {staffDocs.map(doc => (
              <DocumentRow
                key={doc.type}
                type={doc.type}
                label={doc.label}
                required={doc.required}
                document={getDocument(doc.type)}
                onUpload={(file) => handleUpload(doc.type, file)}
                onRemove={() => handleRemove(doc.type)}
                onPreview={() => setPreviewDocument(getDocument(doc.type) || null)}
              />
            ))}
          </div>
        </>
      )}

      {/* Professor E-DEP Documents (for professors only) */}
      {professorDocs.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3 mt-4">
            <span className="text-sm font-medium text-gray-700">
              Documents E-DEP Professeur
              {currentAcademicYear && (
                <span className="ml-2 text-xs text-gray-500">
                  Année {currentAcademicYear}
                </span>
              )}
            </span>
            {professorDocsComplete ? (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Complet
              </span>
            ) : (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                À compléter
              </span>
            )}
          </div>

          {/* Info about annual renewal */}
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Important:</strong> Ces documents doivent être renouvelés chaque année académique.
              {engagementTemplateUrl && (
                <a
                  href={engagementTemplateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 text-primary-600 hover:text-primary-700 underline"
                >
                  Télécharger le modèle d'engagement
                </a>
              )}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg px-4">
            {professorDocs.map(doc => (
              <div key={doc.type}>
                <DocumentRow
                  type={doc.type}
                  label={doc.label}
                  required={doc.required}
                  document={getDocument(doc.type)}
                  onUpload={(file) => handleUpload(doc.type, file)}
                  onRemove={() => handleRemove(doc.type)}
                  onPreview={() => setPreviewDocument(getDocument(doc.type) || null)}
                />
                {doc.description && (
                  <p className="text-xs text-gray-500 ml-4 -mt-2 mb-2">{doc.description}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Preview modal */}
      {previewDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden">
            <button
              onClick={() => setPreviewDocument(null)}
              className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 z-10"
            >
              <X className="w-5 h-5" />
            </button>
            {previewDocument.file.type === 'application/pdf' ? (
              <iframe
                src={previewDocument.previewUrl}
                title="Document preview"
                className="w-full h-[80vh]"
              />
            ) : (
              <img
                src={previewDocument.previewUrl}
                alt="Document preview"
                className="max-w-full max-h-[90vh] object-contain"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
